import React, { useEffect, useRef } from "react";
import jsQR from "jsqr";

export default function QRScanner({ pin, io }) {
  const clientSocketRef = useRef();
  const pinRef = useRef();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (io) {
      clientSocketRef.current = io;
    }
  }, [io]);

  useEffect(() => {
    if (pin) {
      pinRef.current = pin;
    }
  }, [pin]);

  useEffect(() => {
    let stream;
    let animationFrameId;

    const startScanner = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();

        const backCamera = devices.find(
          (device) =>
            device.kind === "videoinput" &&
            /back|rear|environment/i.test(device.label),
        );

        stream = await navigator.mediaDevices.getUserMedia({
          video: backCamera
            ? { deviceId: { exact: backCamera.deviceId } }
            : { facingMode: { ideal: "environment" } },
        });

        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();

        scanQRCode();
      } catch (error) {
        console.error("Camera access error:", error);
      }
    };

    const endScanner = () => {
      if (canvasRef.current) {
        canvasRef.current.remove();
        canvasRef.current = undefined;
      }

      if (videoRef.current) {
        videoRef.current.remove();
        videoRef.current = undefined;
      }

      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        return;
      }
    };

    const scanQRCode = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      const scan = () => {
        if (
          video.readyState === video.HAVE_ENOUGH_DATA &&
          video.videoWidth > 0
        ) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code && code.data.includes(process.env.REACT_APP_FE_HOST)) {
            const dataSplit1 = code.data.split("?");
            const dataSpit2 = dataSplit1[1].split("#");

            const isMultiThreadedStream = dataSpit2[0] === "true";
            const toPin = dataSpit2[1];

            clientSocketRef.current.emit("pair-by-qr-req", {
              to: toPin,
              pin: pinRef.current,
              isMultiThreadedStream: isMultiThreadedStream,
            });

            endScanner();
            return;
          }
        }

        // run scan at roughly 2 fps
        setTimeout(() => {
          animationFrameId = requestAnimationFrame(scan);
        }, 1000 / 2);
      };

      scan();
    };

    startScanner();

    return () => {
      endScanner();
    };
  }, []);

  return (
    <div>
      <video
        id="qr-code-scanner"
        ref={videoRef}
        playsInline
        muted
        style={{
          width: "100%",
          maxWidth: "500px",
          borderRadius: "12px",
        }}
      />

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}
